import type {
  AssetRequirement,
  AssetRequirementKind,
  ManifestFragment
} from "@/app/integration/contracts";
import {
  classifyPublicResourcePath,
  collectLevelResourceReferences,
  formatAssetValidationError,
  manifestFragmentSchema
} from "@/assets/manifest-validation";
import { chineseLevels } from "@/content/chinese";
import { b4ChinesePlayerVoiceTrackDefinitions } from "@/features/domain-players/chinese/audio-tracks";

interface MutableAssetRequirement {
  readonly kind: AssetRequirementKind;
  readonly levelIds: Set<string>;
}

function addRequirement(
  requirements: Map<string, MutableAssetRequirement>,
  path: string,
  kind: AssetRequirementKind,
  levelId: string
): void {
  const existing = requirements.get(path);

  if (existing !== undefined) {
    if (existing.kind !== kind) {
      throw new Error("Conflicting media kinds for Chinese resource " + path);
    }
    existing.levelIds.add(levelId);
    return;
  }

  requirements.set(path, {
    kind,
    levelIds: new Set([levelId])
  });
}

function collectChinesePlayerRequirements(): readonly AssetRequirement[] {
  const requirements = new Map<string, MutableAssetRequirement>();
  const collected = collectLevelResourceReferences(chineseLevels);

  if (collected.errors.length > 0) {
    throw new Error(
      "Invalid Chinese player resource: " +
        collected.errors.map(formatAssetValidationError).join("; ")
    );
  }

  for (const reference of collected.references) {
    addRequirement(
      requirements,
      reference.path,
      reference.kind,
      reference.levelId
    );
  }

  for (const definition of b4ChinesePlayerVoiceTrackDefinitions) {
    const kind = classifyPublicResourcePath(definition.audioKey);
    if (kind !== "audio") {
      throw new Error(
        "Invalid Chinese player voice key " + definition.audioKey
      );
    }
    addRequirement(
      requirements,
      definition.audioKey,
      kind,
      definition.levelId
    );
  }

  return [...requirements.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([path, { kind, levelIds }]) => ({
      kind,
      levelIds: [...levelIds].sort(),
      path
    }));
}

export const b4ChinesePlayerManifest: ManifestFragment =
  manifestFragmentSchema.parse({
    owner: "chinese-domain-player-b4",
    requirements: collectChinesePlayerRequirements()
  });

export function isB4ChinesePlayerAsset(
  levelId: string,
  path: string,
  kind: AssetRequirementKind
): boolean {
  return b4ChinesePlayerManifest.requirements.some(
    (requirement) =>
      requirement.kind === kind &&
      requirement.path === path &&
      requirement.levelIds.includes(levelId)
  );
}
